import { DateTime } from 'luxon'
import { CompanyAssociation } from 'store/company/company.types'
import {
  InvoiceDurationType,
  InvoiceEvent,
  InvoiceLineItem,
  InvoiceResponse,
  InvoiceType,
  TransformInvoiceResponse,
} from 'store/invoices/invoices.types'
import { EventType } from 'store/orders/orders.types'

import type { InvoiceCustomerScope } from './invoice-workspace.context'

export function factoringCompanyIdSet(
  associations: CompanyAssociation[] | undefined
): Set<string> {
  return new Set(
    (associations ?? [])
      .filter((a) => a.factoringEnabled)
      .map((a) => a.companyId)
  )
}

export type PodOrderInfo = { resolved: boolean; hasPod: boolean }

/** Split TO_BE_INVOICED rows by POD upload state for To Deliver / To Invoice tabs. */
export function filterTransformInvoiceGroupsByPodSubTab(
  groups: TransformInvoiceResponse[],
  invoiceType: InvoiceType,
  podByOrderId: Record<string, PodOrderInfo>
): TransformInvoiceResponse[] {
  if (invoiceType !== 'TO_DELIVER' && invoiceType !== 'TO_INVOICE') {
    return groups
  }

  return groups
    .map((group) => ({
      ...group,
      invoiceData: group.invoiceData.filter((inv) => {
        const orderId = inv.orderId ?? ''
        const info = podByOrderId[orderId] ?? { resolved: false, hasPod: false }
        if (invoiceType === 'TO_DELIVER') {
          return !info.resolved || !info.hasPod
        }
        return info.resolved && info.hasPod
      }),
    }))
    .filter((group) => group.invoiceData.length > 0)
}

export function filterTransformInvoiceGroups(
  groups: TransformInvoiceResponse[] | undefined,
  scope: InvoiceCustomerScope,
  factoringIds: Set<string> | undefined
): TransformInvoiceResponse[] {
  if (!groups?.length) return []
  if (factoringIds === undefined) {
    return scope === 'factoring' ? [] : groups
  }
  return groups.filter((group) => {
    const customerId = group.invoiceData[0]?.customerId
    if (!customerId) return scope === 'standard'
    const isFactoring = factoringIds.has(customerId)
    return scope === 'factoring' ? isFactoring : !isFactoring
  })
}

export const formatEventType = (eventType: EventType) => {
  switch (eventType) {
    case 'PICKUP':
      return 'Picked up'
    case 'DELIVERY':
      return 'Delivered'
    default:
      return ''
  }
}

export const convertTimestampToDateAndTime = (
  timestamp?: string | null
): { date: string; time: string } => {
  const date = timestamp
    ? DateTime.fromISO(timestamp).toFormat('MMM dd, yyyy')
    : ''
  const time = timestamp ? DateTime.fromISO(timestamp).toFormat('HH:mm') : ''

  return { date, time }
}

export function getInvoiceAgingDays(invoice: InvoiceResponse): number {
  if (invoice.status === 'TO_BE_INVOICED') {
    return 0
  }

  const invoiceSentEvent = invoice.events.find(
    (item) => item.event === InvoiceEvent.InvoiceSent && item.createdDate
  )

  if (!invoiceSentEvent?.createdDate) {
    return 0
  }

  const givenDate = DateTime.fromISO(invoiceSentEvent.createdDate)
  const today = DateTime.now()

  return Math.round(today.diff(givenDate, 'days').days)
}

export function matchesInvoiceAgingFilter(
  invoice: InvoiceResponse,
  invoiceType: InvoiceType,
  duration: InvoiceDurationType
): boolean {
  if (invoiceType !== 'NOT_PAID' || duration === 'All') {
    return true
  }

  const aging = getInvoiceAgingDays(invoice)

  switch (duration) {
    case 'Under30':
      return aging <= 29
    case 'Over30':
      return aging >= 30 && aging <= 44
    case 'Over45':
      return aging >= 45 && aging <= 59
    case 'Over60':
      return aging >= 60 && aging <= 89
    case 'Over90':
      return aging >= 90
    default:
      return true
  }
}

export const subTotal = (lineItems: InvoiceLineItem[]): number => {
  return lineItems.reduce((acc, item) => {
    const totalWithTax = item.tax
      ? item.lineTotal + item.taxAmount
      : item.lineTotal
    return acc + totalWithTax
  }, 0)
}

export const total = (lineItems: InvoiceLineItem[]): number => {
  return lineItems.reduce((acc, item) => {
    const totalWithTax = item.tax
      ? item.lineTotal + item.taxAmount
      : item.lineTotal
    return acc + totalWithTax
  }, 0)
}
